import React, { memo, useCallback } from 'react'

import { isEqual } from 'date-fns'
import { useTheme } from 'styled-components'

import { Loading } from '@components/Loading'
import { Separator } from '@components/Separator'
import { MidstreamTile } from '@containers/Task/Tiles/MidstreamTile'
import { useSharedDates } from '@contexts/sharedDates'
import { useDate } from '@hooks/dates'
import { useFindTasks } from '@hooks/tasks/find'

import { LoaderContainer, NoTasksText } from './styles'
import { MidstreamSwipe } from '../Actions/MidstreamSwipe'

export const MidstreamTaskList = memo(function MidstreamTaskList() {
  const theme = useTheme()

  const { getWeekRange, getSiteStartTime } = useDate()
  const { selectedDate, selectedWeekDay } = useSharedDates()
  const { weekStart, weekEnd } = getWeekRange(selectedDate)
  const todoStartTime = getSiteStartTime(5, selectedWeekDay.date)

  const {
    data: rawTasks,
    refetch,
    isLoading,
    isFetching,
    isPaused,
  } = useFindTasks({
    startDate: weekStart,
    endDate: weekEnd,
    view: 'midstream',
  })

  const tasks = rawTasks ?? []
  type TTask = (typeof tasks)[number]

  const timeResults = useCallback(
    ({ startTime, status }: TTask) =>
      !isEqual(new Date(startTime), todoStartTime) &&
      isEqual(new Date(startTime).getDay(), selectedWeekDay.date.getDay()) &&
      status !== 'imported',
    [selectedWeekDay, todoStartTime],
  )

  const dayTasks = tasks
    .filter(timeResults)
    .sort(
      (a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    )

  const renderItem = useCallback(
    (task: TTask) => (
      <MidstreamSwipe task={task}>
        <MidstreamTile task={task} />
      </MidstreamSwipe>
    ),
    [],
  )

  return (
    <>
      {isLoading ? (
        <LoaderContainer>
          <Loading color={theme.colors.text} size="small" />
        </LoaderContainer>
      ) : dayTasks.length > 0 ? (
        <div>
          {dayTasks.map((task, index) => (
            <div key={task.id.toString()}>
              {index > 0 && <Separator />}
              {renderItem(task)}
            </div>
          ))}
        </div>
      ) : (
        <LoaderContainer>
          <NoTasksText>Oops, no tasks available.</NoTasksText>
        </LoaderContainer>
      )}
    </>
  )
})
